import { deleteData, getData, patchData, postData } from "../api/api";
import type {
  Widget,
  WidgetExecuteRequest,
  WidgetExecuteResponse,
  WidgetValidationResponse,
  WidgetDslProbeResponse,
} from "../types/widget";
import type { PageResponse } from "../types/dashboard";
import { sanitizeWidgetForImport } from "../utils/sanitizeWidgetImport";

export type MappingFieldDescriptor = {
  path: string;
  type: string;
  aggregatable: boolean;
  notes?: string;
};

export type WidgetMappingHintsResponse = {
  indexName: string;
  mappingHash?: string;
  fieldCount: number;
  fields: MappingFieldDescriptor[];
};

const BASE = "/widgets";

export const widgetService = {
  list: (page = 0, size = 20) =>
    getData(`${BASE}?page=${page}&size=${size}`) as Promise<PageResponse<Widget>>,

  get: (id: string) => getData(`${BASE}/${id}`) as Promise<Widget>,

  getMappingHints: (index: string) =>
    getData(`${BASE}/mapping-hints?index=${encodeURIComponent(index)}`) as Promise<WidgetMappingHintsResponse>,

  searchIndices: (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      return Promise.resolve([] as string[]);
    }
    return getData(`${BASE}/indices?q=${encodeURIComponent(q)}`) as Promise<string[]>;
  },

  create: (widget: Widget) => postData(BASE, widget) as Promise<Widget>,

  update: (id: string, widget: Widget) => patchData(`${BASE}/${id}`, widget) as Promise<Widget>,

  remove: (id: string) => deleteData(`${BASE}/${id}`),

  execute: (id: string, body: WidgetExecuteRequest) =>
    postData(`${BASE}/${id}/execute`, body) as Promise<WidgetExecuteResponse>,

  validate: (widget: Widget) =>
    postData(`${BASE}/validate`, widget) as Promise<WidgetValidationResponse>,

  validateExecute: (id: string, body: WidgetExecuteRequest) =>
    postData(`${BASE}/${id}/validate-execute`, body) as Promise<WidgetValidationResponse>,

  preview: async (widget: Widget, body: WidgetExecuteRequest): Promise<WidgetValidationResponse> =>
    postData(`${BASE}/validate-execute`, {
      widget,
      from: body.from,
      to: body.to,
      params: body.params,
      sourceRanges: body.sourceRanges,
    }) as Promise<WidgetValidationResponse>,

  probeDsl: async (widget: Widget, body: WidgetExecuteRequest): Promise<WidgetDslProbeResponse> =>
    postData(`${BASE}/dsl-probe`, {
      widget,
      from: body.from,
      to: body.to,
      params: body.params,
      sourceRanges: body.sourceRanges,
    }) as Promise<WidgetDslProbeResponse>,

  exportJson: (widget: Widget) => {
    const blob = new Blob([JSON.stringify(widget, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${widget.name || "widget"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  importJson: async (file: File): Promise<Widget> => {
    const text = await file.text();
    const parsed = JSON.parse(text) as Widget;
    return widgetService.create(sanitizeWidgetForImport(parsed));
  },

  listVersions: (id: string, page = 0, size = 20) =>
    getData(`${BASE}/${id}/versions?page=${page}&size=${size}`) as Promise<PageResponse<WidgetVersionSummary>>,

  restoreVersion: (id: string, versionId: string) =>
    postData(`${BASE}/${id}/versions/${versionId}/restore`, {}) as Promise<Widget>,
};

export type WidgetVersionSummary = {
  id: string;
  widgetId: string;
  versionNumber: number;
  savedAt?: string;
  savedBy?: string;
  widgetName?: string;
};
